import React, { useState, useCallback, useEffect } from 'react';
import { useApi } from '@/shared/hooks/useApi';
import nominationService from '@/services/api/nominations';
import type { Nomination } from '@/types/models/schema';
import type { FilterOptions, ApiResponse } from '@/types/api';
import { AxiosInstance } from 'axios';

/**
 * Simplified useNominations Hook
 * 
 * Provides data and operations for managing podcast guest nominations
 */
export function useNominations(initialFilters?: FilterOptions) {
  const [filters, setFilters] = useState<FilterOptions>(initialFilters || {});
  
  // Use the updated useApi hook pattern with correct signature
  const {
    data: nominations,
    isLoading,
    error,
    refetch,
    retry
  } = useApi<Nomination[]>(
    useCallback(async (client: AxiosInstance) => {
      return nominationService.getAll(filters);
    }, [filters])
  );
  
  const fetchNominations = useCallback(async (filterOptions?: FilterOptions) => {
    if (filterOptions) {
      setFilters(filterOptions);
    }
    return refetch();
  }, [refetch]);
  
  const getNominationById = useCallback(async (id: string): Promise<ApiResponse<Nomination>> => {
    const rawResponse = await nominationService.getById(id);
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<Nomination> = {
      data: rawResponse,
      error: rawResponse === null ? new Error(`Failed to fetch nomination with ID ${id}`) : null
    };
    
    return response;
  }, []);
  
  const createNomination = useCallback(async (data: Omit<Nomination, 'id' | 'created_at'>): Promise<ApiResponse<Nomination>> => {
    const rawResponse = await nominationService.create(data);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<Nomination> = {
      data: rawResponse,
      error: rawResponse === null ? new Error('Failed to create nomination') : null
    };
    
    return response;
  }, [refetch]);
  
  const updateNomination = useCallback(async (id: string, data: Partial<Nomination>): Promise<ApiResponse<Nomination>> => {
    const rawResponse = await nominationService.update(id, data);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<Nomination> = {
      data: rawResponse,
      error: rawResponse === null ? new Error(`Failed to update nomination with ID ${id}`) : null
    };
    
    return response;
  }, [refetch]);
  
  const deleteNomination = useCallback(async (id: string): Promise<ApiResponse<boolean>> => {
    const rawResponse = await nominationService.delete(id);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<boolean> = {
      data: rawResponse,
      error: rawResponse === false ? new Error(`Failed to delete nomination with ID ${id}`) : null
    };
    
    return response;
  }, [refetch]);
  
  const convertToGuest = useCallback(async (id: string): Promise<ApiResponse<boolean>> => {
    const rawResponse = await nominationService.convertToGuest(id);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<boolean> = {
      data: rawResponse,
      error: rawResponse === false ? new Error(`Failed to convert nomination with ID ${id} to guest`) : null
    };
    
    return response;
  }, [refetch]);
  
  // Load nominations on mount and when filters change
  useEffect(() => {
    fetchNominations();
  }, [fetchNominations]);
  
  return {
    nominations,
    isLoading,
    error,
    filters,
    setFilters,
    fetchNominations,
    getNominationById,
    createNomination,
    updateNomination,
    deleteNomination,
    convertToGuest,
    refetchNominations: refetch,
    retryFetch: retry
  };
}

export default useNominations;
